import {
    IProduct,
    IVariant,
    IRatingsReviewsConfig,
    IComponents,
    IPRProductProduct,
    ISkuData
} from '../../interfaces/IRating';
import {
    REVIEW_DISPLAY,
    QUESTION_DISPLAY,
    SNAPSHOT_TYPE,
    PAGINATION_TYPE
} from '../../constants/PowerReviews';
import { emit, events } from '@estee/elc-service-bus';
import { REVIEW_CATEGORY } from '../../constants/ServiceReviews';
import { InventoryStatuses } from '../../constants/InventoryStatuses';

export class PowerReviewsProduct {
    public api_key: string;
    public locale: string;
    public merchant_group_id: string;
    public merchant_id: string;
    public page_id: string;
    public review_wrapper_url: string;
    public components: IComponents = {
        ReviewDisplay: REVIEW_DISPLAY,
        QuestionDisplay: QUESTION_DISPLAY
    };
    public REVIEW_DISPLAY_SNAPSHOT_TYPE: string;
    public REVIEW_DISPLAY_PAGINATION_TYPE = PAGINATION_TYPE.DEFAULT;
    public variants: IVariant[];
    public product: IPRProductProduct;
    public on_submit: () => void;

    public static fromJson(
        data: IProduct,
        ratingsReviewsConfig: IRatingsReviewsConfig,
        paginationType: string,
        selectedSkuId?: string,
        prDisclosureCode?: string
    ): PowerReviewsProduct {
        const prProduct = new PowerReviewsProduct();

        const {
            apiKey,
            locale,
            merchantGroupId,
            merchantId,
            pageId,
            reviewWrapperUrl,
            reviewDisplaySnapshotType,
            brandName
        } = ratingsReviewsConfig;

        const {
            productId,
            displayName,
            productUrl,
            shortDescription,
            defaultCategory,
            defaultSku: { price, originalPrice },
            skus,
            defaultSku
        } = data;

        const baseUrl = `${window.location.protocol}//${document.location.hostname}`;
        const prPageId = pageId || productId;

        prProduct.api_key = apiKey;
        prProduct.locale = locale;
        prProduct.merchant_group_id = merchantGroupId;
        prProduct.merchant_id = merchantId;
        prProduct.page_id = prPageId;
        prProduct.review_wrapper_url = prProduct.reviewUrl(
            reviewWrapperUrl,
            prPageId,
            prDisclosureCode
        );
        prProduct.REVIEW_DISPLAY_SNAPSHOT_TYPE = reviewDisplaySnapshotType || SNAPSHOT_TYPE;

        // Converting boolean to string as requested by PowerReviews
        const inStock = prProduct.isInStock(data, selectedSkuId).toString();

        prProduct.product = {
            name: displayName,
            url: baseUrl.concat(productUrl),
            image_url: prProduct.getDefaultImage(defaultSku),
            description: shortDescription,
            category_name: defaultCategory.id,
            brand_name: brandName,
            price: price || originalPrice,
            in_stock: inStock
        };

        if (skus.length > 1) {
            prProduct.product.variants = prProduct.productVariants(data);
        } else {
            prProduct.product.upc = defaultSku.upc;
        }

        if (paginationType === PAGINATION_TYPE.VERTICAL) {
            prProduct.REVIEW_DISPLAY_PAGINATION_TYPE = paginationType;
        }

        prProduct.on_submit = function () {
            prProduct.onPrFormSubmit(data);
        };

        return prProduct;
    }

    private isInStock(data: IProduct, selectedSkuId?: string) {
        const { isOutOfStock, skus } = data;

        if (selectedSkuId) {
            const selectedSku = skus.find((sku) => sku.skuId === selectedSkuId);
            if (selectedSku) {
                return selectedSku.inventoryStatus === InventoryStatuses.Active;
            }
        }

        return !isOutOfStock;
    }

    private onPrFormSubmit(data: IProduct) {
        const payLoad = {
            id: data.productId,
            name: data.displayName,
            reviewCategory: REVIEW_CATEGORY
        };
        emit(events.ASK_AND_ANSWER_SUBMITTED, { ...payLoad });
    }

    private reviewUrl(reviewWrapperUrl: string, pageId: string, prDisclosureCode?: string): string {
        const url = reviewWrapperUrl || '/review/create';
        let queryString = `?pr_page_id=${pageId}`;
        if (prDisclosureCode) {
            queryString = `${queryString}&pr_disclosure_code=${prDisclosureCode}`;
        }

        return `${url}${queryString}`;
    }

    private productVariants(data: IProduct) {
        const isShaded = data.isShaded;

        return data.skus.reduce((variantAcc: IVariant[], sku) => {
            variantAcc.push({
                name: this.getVariantName(sku, isShaded) || '',
                image_url: this.getDefaultImage(sku),
                upc: sku.upc,
                page_id_variant: sku.perlgem.SKU_BASE_ID
            });

            return variantAcc;
        }, []);
    }

    private getVariantName(sku: ISkuData, isShaded: boolean) {
        if (isShaded) {
            return sku.shades.length && sku.shades[0].name;
        }

        return sku.sizes.length && sku.sizes[0].value;
    }

    private getDefaultImage(sku: ISkuData) {
        const baseUrl = `${window.location.protocol}//${document.location.hostname}`;
        const { defaultLargeImage, defaultMediumImage } = sku;

        if (defaultLargeImage && defaultLargeImage.src) {
            return baseUrl.concat(defaultLargeImage.src);
        }

        if (defaultMediumImage && defaultMediumImage.src) {
            return baseUrl.concat(defaultMediumImage.src);
        }

        return '';
    }
}
